import { atom, useAtom } from "jotai";

import {
  addDaysToIsoDate,
  getClosestUpcomingTuesday,
} from "@features/mmm/utils/get_cycle_weeks";
import { FETCH_MEDIA_PLAN_DATA } from "@gql/mmm-media-plan";
import { useNorthbeamQuery } from "@hooks/http";
import { type FetchMediaPlanData } from "@north-beam/nb-client-side/nb-api-graphql-generated";

import { useIsAutoblessedModelsEnabled } from "./use-is-autoblessed-models-enabled";

const mediaPlanDataAtom = atom<
  Omit<FetchMediaPlanData["me"]["mmm"]["mediaPlanData"], "__typename">
>({
  mediaPlanList: [],
  modelConfigurations: [],
});
const modelConfigurationsAtom = atom(
  (get) => get(mediaPlanDataAtom).modelConfigurations,
  (get, set, callbackOrValue) => {
    const oldModelConfigurations = get(mediaPlanDataAtom).modelConfigurations;
    const newModelConfigurations =
      typeof callbackOrValue === "function"
        ? callbackOrValue(oldModelConfigurations)
        : callbackOrValue;
    set(mediaPlanDataAtom, {
      ...get(mediaPlanDataAtom),
      modelConfigurations: newModelConfigurations,
    });
  },
);
const mediaPlanListAtom = atom(
  (get) =>
    get(mediaPlanDataAtom).mediaPlanList.map((mediaPlan) => {
      return {
        ...mediaPlan,
        date_range: {
          ...mediaPlan.date_range,
          end_date: addDaysToIsoDate(
            getClosestUpcomingTuesday(mediaPlan.date_range.end_date),
            -1,
          ),
        },
      };
    }),
  (get, set, callbackOrValue) => {
    const fixDatesOnMediaPlanList = (
      mediaPlanList: FetchMediaPlanData["me"]["mmm"]["mediaPlanData"]["mediaPlanList"],
    ) => {
      return mediaPlanList.map((mediaPlan) => ({
        ...mediaPlan,
        date_range: {
          ...mediaPlan.date_range,
          end_date: getClosestUpcomingTuesday(mediaPlan.date_range.end_date),
        },
      }));
    };
    const oldMediaPlanList = get(mediaPlanDataAtom).mediaPlanList;
    const newMediaPlanList =
      typeof callbackOrValue === "function"
        ? callbackOrValue(oldMediaPlanList)
        : callbackOrValue;
    set(mediaPlanDataAtom, {
      ...get(mediaPlanDataAtom),
      mediaPlanList: fixDatesOnMediaPlanList(newMediaPlanList),
    });
  },
);

export const useMediaPlanData = () => {
  const isAutoblessedModelsEnabled = useIsAutoblessedModelsEnabled();
  const [mediaPlanList, setMediaPlanList] = useAtom(mediaPlanListAtom);
  const [modelConfigurations] = useAtom(modelConfigurationsAtom);
  const [_, setMediaPlanData] = useAtom(mediaPlanDataAtom);

  const { loading: isLoading } = useNorthbeamQuery<FetchMediaPlanData>(
    FETCH_MEDIA_PLAN_DATA,
    {
      skip: isAutoblessedModelsEnabled,
      notifyOnNetworkStatusChange: true,
      onCompleted: (data) => {
        if (data?.me?.mmm?.mediaPlanData) {
          if (
            data.me.mmm.mediaPlanData.mediaPlanList.length >
            mediaPlanList.length
          ) {
            setMediaPlanData(data.me.mmm.mediaPlanData);
          }
        }
      },
    },
  );

  return {
    mediaPlanList,
    modelConfigurations,
    isLoading,
    setMediaPlanList,
  };
};
